import useIndustrialTasks from "../Provider/useIndustrialTasks";
import styled from "styled-components";
import {Colors} from "../Styles/Colors";
import {GiCargoCrate, GiMining, GiOre} from "react-icons/gi";
import {MdAccessTime, MdCheckCircleOutline, MdLocationOn, MdOutlineInventory2} from "react-icons/md";
import {iIndustrialJob} from "../Provider/useIndustrialTasksContextProvider";
import {useMemo, useState} from "react";

const Styles = {
	Page: styled.div`
		padding: 28px;
		color: ${Colors.raw.terminal_light};
		text-align: left;
	`,

	Header: styled.div`
		display: flex;
		justify-content: space-between;
		align-items: flex-end;
		gap: 18px;
		margin-bottom: 24px;

		@media (max-width: 840px) {
			align-items: flex-start;
			flex-direction: column;
		}
	`,

	TitleBlock: styled.div`
		display: grid;
		gap: 8px;
	`,

	Eyebrow: styled.div`
		color: ${Colors.raw.neon_orange};
		letter-spacing: 3px;
		text-transform: uppercase;
		font-size: 12px;
	`,

	Title: styled.h1`
		margin: 0;
		font-size: clamp(30px, 5vw, 54px);
		line-height: 1;
		text-transform: uppercase;
		text-shadow: 0 0 18px rgba(255, 122, 0, 0.45);
	`,

	Subline: styled.div`
		max-width: 760px;
		opacity: 0.72;
		font-size: 14px;
		line-height: 1.55;
	`,

	Summary: styled.div`
		display: grid;
		grid-template-columns: repeat(3, minmax(110px, 1fr));
		gap: 10px;

		@media (max-width: 560px) {
			width: 100%;
			grid-template-columns: 1fr;
		}
	`,

	SummaryBox: styled.div`
		border: 1px solid rgba(255, 153, 51, 0.38);
		background:
			radial-gradient(circle at top right, rgba(255, 122, 0, 0.18), transparent 42%),
			rgba(0, 0, 0, 0.25);
		padding: 12px 14px;
		min-width: 120px;
		box-shadow: 0 0 16px rgba(255, 122, 0, 0.10);
	`,

	SummaryLabel: styled.div`
		opacity: 0.58;
		font-size: 10px;
		text-transform: uppercase;
		letter-spacing: 1.5px;
		margin-bottom: 6px;
	`,

	SummaryValue: styled.div`
		color: ${Colors.raw.neon_orange};
		font-family: "Courier New", monospace;
		font-size: 18px;
		font-weight: 800;
	`,

	Tabs: styled.div`
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		margin-bottom: 20px;
	`,

	TabButton: styled.button<{ $active: boolean }>`
		border: 1px solid ${({ $active }) =>
		$active ? Colors.raw.neon_orange : "rgba(255, 153, 51, 0.32)"};
		background: ${({ $active }) =>
		$active ? "rgba(255, 122, 0, 0.22)" : "rgba(0, 0, 0, 0.25)"};
		color: ${({ $active }) =>
		$active ? Colors.raw.neon_orange : Colors.raw.terminal_light};
		padding: 10px 14px;
		cursor: pointer;
		font-family: "Courier New", monospace;
		font-size: 12px;
		font-weight: 800;
		text-transform: uppercase;
		letter-spacing: 1px;

		&:hover {
			border-color: ${Colors.raw.neon_orange};
			box-shadow: 0 0 12px rgba(255, 122, 0, 0.22);
		}
	`,

	SectionTitle: styled.div`
		display: flex;
		align-items: center;
		gap: 10px;
		margin: 18px 0 14px 0;
		color: ${Colors.raw.neon_orange};
		text-transform: uppercase;
		letter-spacing: 2px;
		font-size: 12px;

		&::after {
			content: "";
			height: 1px;
			flex: 1;
			background: rgba(255, 153, 51, 0.28);
		}
	`,

	Grid: styled.div`
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
		gap: 18px;
	`,

	Card: styled.article<{ $completed?: boolean }>`
		position: relative;
		overflow: hidden;
		display: flex;
		flex-direction: column;
		gap: 14px;
		padding: 18px;

		border: 1px solid ${({ $completed }) =>
		$completed ? "rgba(67, 255, 157, 0.42)" : "rgba(255, 153, 51, 0.38)"};
		background:
			radial-gradient(circle at top right, ${({ $completed }) =>
		$completed ? "rgba(67, 255, 157, 0.16)" : "rgba(255, 122, 0, 0.20)"}, transparent 36%),
			linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.018));
		box-shadow: 0 0 20px ${({ $completed }) =>
		$completed ? "rgba(67, 255, 157, 0.10)" : "rgba(255, 122, 0, 0.10)"};
		clip-path: polygon(0 0, calc(100% - 22px) 0, 100% 22px, 100% 100%, 0 100%);

		&::before {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			height: 3px;
			width: 100%;
			background: ${({ $completed }) =>
		$completed ? "#43ff9d" : Colors.raw.neon_orange};
		}
	`,

	CardTop: styled.div`
		display: grid;
		grid-template-columns: auto 1fr auto;
		gap: 12px;
		align-items: flex-start;
	`,

	IconBox: styled.div<{ $completed?: boolean }>`
		width: 46px;
		height: 46px;
		display: grid;
		place-items: center;
		border: 1px solid ${({ $completed }) =>
		$completed ? "rgba(67, 255, 157, 0.45)" : "rgba(255, 153, 51, 0.42)"};
		background: ${({ $completed }) =>
		$completed ? "rgba(67, 255, 157, 0.10)" : "rgba(255, 122, 0, 0.12)"};
		color: ${({ $completed }) =>
		$completed ? "#8dffc5" : Colors.raw.neon_orange};
		box-shadow: 0 0 14px rgba(255, 122, 0, 0.12);

		svg {
			width: 26px;
			height: 26px;
		}
	`,

	JobTitleBlock: styled.div`
		min-width: 0;
	`,

	JobTitle: styled.h2`
		margin: 0;
		color: ${Colors.raw.terminal_light};
		font-size: 18px;
		line-height: 1.2;
		word-break: break-word;
	`,

	JobSubline: styled.div`
		margin-top: 6px;
		color: ${Colors.raw.neon_orange};
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 1.5px;
		line-height: 1.35;
	`,

	StatusBadge: styled.div<{ $completed?: boolean }>`
		flex-shrink: 0;
		border: 1px solid ${({ $completed }) =>
		$completed ? "rgba(67, 255, 157, 0.45)" : "rgba(255, 153, 51, 0.45)"};
		background: ${({ $completed }) =>
		$completed ? "rgba(67, 255, 157, 0.10)" : "rgba(255, 122, 0, 0.12)"};
		color: ${({ $completed }) =>
		$completed ? "#8dffc5" : Colors.raw.neon_orange};
		padding: 7px 9px;
		font-size: 11px;
		font-weight: 900;
		font-family: "Courier New", monospace;
		text-transform: uppercase;
		white-space: nowrap;
	`,

	Stats: styled.div`
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 8px;

		@media (max-width: 560px) {
			grid-template-columns: 1fr;
		}
	`,

	Stat: styled.div`
		border: 1px solid rgba(255, 255, 255, 0.12);
		background: rgba(0, 0, 0, 0.22);
		padding: 10px;
		min-width: 0;
	`,

	StatLabel: styled.div`
		display: flex;
		align-items: center;
		gap: 6px;
		opacity: 0.58;
		font-size: 10px;
		text-transform: uppercase;
		letter-spacing: 1.4px;
		margin-bottom: 5px;

		svg {
			width: 13px;
			height: 13px;
		}
	`,

	StatValue: styled.div`
		color: ${Colors.raw.terminal_light};
		font-size: 13px;
		font-weight: 800;
		word-break: break-word;
	`,

	ProgressBar: styled.div`
		height: 8px;
		border: 1px solid rgba(255, 153, 51, 0.28);
		background: rgba(0, 0, 0, 0.35);
		overflow: hidden;
	`,

	ProgressFill: styled.div<{ $progress: number; $completed?: boolean }>`
		height: 100%;
		width: ${({ $progress }) => `${Math.min(100, Math.max(0, $progress))}%`};
		background: ${({ $completed }) =>
		$completed ? "#43ff9d" : Colors.raw.neon_orange};
		box-shadow: 0 0 12px ${({ $completed }) =>
		$completed ? "rgba(67, 255, 157, 0.45)" : "rgba(255, 122, 0, 0.45)"};
	`,

	CardFooter: styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 12px;
		border-top: 1px solid rgba(255, 255, 255, 0.10);
		padding-top: 12px;
		flex-wrap: wrap;
	`,

	FooterText: styled.div`
		opacity: 0.58;
		font-size: 11px;
		font-family: "Courier New", monospace;
	`,

	CompleteButton: styled.button`
		display: inline-flex;
		align-items: center;
		gap: 8px;
		border: 1px solid rgba(67, 255, 157, 0.52);
		background: rgba(67, 255, 157, 0.12);
		color: #8dffc5;
		padding: 9px 12px;
		cursor: pointer;
		font-family: "Courier New", monospace;
		font-size: 11px;
		font-weight: 900;
		text-transform: uppercase;
		letter-spacing: 1px;

		svg {
			width: 16px;
			height: 16px;
		}

		&:hover {
			box-shadow: 0 0 14px rgba(67, 255, 157, 0.25);
		}
	`,

	DisabledButton: styled.button`
		border: 1px solid rgba(255, 255, 255, 0.14);
		background: rgba(255, 255, 255, 0.035);
		color: rgba(255, 255, 255, 0.38);
		padding: 9px 12px;
		cursor: not-allowed;
		font-family: "Courier New", monospace;
		font-size: 11px;
		font-weight: 900;
		text-transform: uppercase;
		letter-spacing: 1px;
	`,

	EmptyState: styled.div`
		padding: 26px;
		border: 1px dashed rgba(255, 255, 255, 0.22);
		background: rgba(255, 255, 255, 0.035);
		opacity: 0.76;
		text-align: center;
		font-size: 13px;
	`,
};

type Tab = "all" | "inProgress" | "completed";

const getJobEndDate = (job: iIndustrialJob) =>
	new Date(new Date(job.timestamp).getTime() + job.durationInMinutes * 60 * 1000);

const formatDateTime = (value: Date) =>
	new Date(value).toLocaleString("de-DE", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});

const formatDuration = (milliseconds: number) => {
	const safeMilliseconds = Math.max(0, milliseconds);
	const totalSeconds = Math.ceil(safeMilliseconds / 1000);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	if (hours > 0) {
		return `${hours}h ${minutes}m`;
	}

	if (minutes > 0) {
		return `${minutes}m ${seconds}s`;
	}

	return `${seconds}s`;
};

const getProgress = (job: iIndustrialJob) => {
	const start = new Date(job.timestamp).getTime();
	const end = getJobEndDate(job).getTime();
	const now = Date.now();

	if (end <= start) {
		return 100;
	}

	return ((now - start) / (end - start)) * 100;
};

const JobCard = ({
					 job,
					 completed,
					 onComplete,
				 }: {
	job: iIndustrialJob;
	completed: boolean;
	onComplete: (id: string) => void;
}) => {

	const { materials } = useIndustrialTasks()
	const endDate = getJobEndDate(job);
	const remainingMilliseconds = endDate.getTime() - Date.now();
	const progress = completed ? 100 : getProgress(job);

	return (
		<Styles.Card $completed={completed}>
			<Styles.CardTop>
				<Styles.IconBox $completed={completed}>
					{completed ? <MdCheckCircleOutline /> : <GiMining />}
				</Styles.IconBox>

				<Styles.JobTitleBlock>
					<Styles.JobTitle>{ job.title }</Styles.JobTitle>
					<Styles.JobSubline>{job.location}</Styles.JobSubline>
				</Styles.JobTitleBlock>

				<Styles.StatusBadge $completed={completed}>
					{completed ? "Fertig" : "Läuft"}
				</Styles.StatusBadge>
			</Styles.CardTop>

			<Styles.Stats>
				<Styles.Stat>
					<Styles.StatLabel>
						<GiOre />
						Material
					</Styles.StatLabel>
					<Styles.StatValue>{ materials.find( a => a.id === job.material)?.name }</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>
						<MdOutlineInventory2 />
						Menge
					</Styles.StatLabel>
					<Styles.StatValue>{job.amount} SCU</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>
						<MdAccessTime />
						Dauer
					</Styles.StatLabel>
					<Styles.StatValue>{job.durationInMinutes} Minuten</Styles.StatValue>
				</Styles.Stat>

				<Styles.Stat>
					<Styles.StatLabel>
						<MdLocationOn />
						{completed ? "Abgeschlossen" : "Restzeit"}
					</Styles.StatLabel>
					<Styles.StatValue>
						{completed ? formatDateTime(endDate) : formatDuration(remainingMilliseconds)}
					</Styles.StatValue>
				</Styles.Stat>
			</Styles.Stats>

			<Styles.ProgressBar>
				<Styles.ProgressFill $progress={progress} $completed={completed} />
			</Styles.ProgressBar>

			<div>{ job.note ?? "" }</div>

			<Styles.CardFooter>
				<Styles.FooterText>
					Start: {formatDateTime(job.timestamp)} · Ende: {formatDateTime(endDate)}
				</Styles.FooterText>

				{completed ? (
					<Styles.CompleteButton
						type="button"
						onClick={() => onComplete(job.id)}
					>
						<MdCheckCircleOutline />
						Abholen / Abschließen
					</Styles.CompleteButton>
				) : (
					<Styles.DisabledButton type="button" disabled>
						Noch in Arbeit
					</Styles.DisabledButton>
				)}
			</Styles.CardFooter>
		</Styles.Card>
	);
};

export const IndustrialMyJobs = () => {
	const {jobs, inProgress, completedTasks, Actions} = useIndustrialTasks();
	const [activeTab, setActiveTab] = useState<Tab>("all");

	const visibleJobs = useMemo(() => {
		switch (activeTab) {
			case "inProgress":
				return inProgress;
			case "completed":
				return completedTasks;
			case "all":
				return [...inProgress, ...completedTasks];
		}
	}, [activeTab, completedTasks, inProgress]);

	const completedIds = useMemo(
		() => new Set(completedTasks.map(job => job.id)),
		[completedTasks]
	);

	return (
		<Styles.Page>
			<Styles.Header>
				<Styles.TitleBlock>
					<Styles.Eyebrow>Industrial Orders</Styles.Eyebrow>
					<Styles.Title>Meine Aufträge</Styles.Title>
					<Styles.Subline>
						Übersicht aller Industrie-Aufträge. Laufende Aufträge bleiben aktiv,
						abgeschlossene Aufträge können abgeholt und dadurch aus der Liste
						entfernt werden.
					</Styles.Subline>
				</Styles.TitleBlock>

				<Styles.Summary>
					<Styles.SummaryBox>
						<Styles.SummaryLabel>Alle</Styles.SummaryLabel>
						<Styles.SummaryValue>{jobs.length}</Styles.SummaryValue>
					</Styles.SummaryBox>

					<Styles.SummaryBox>
						<Styles.SummaryLabel>In Arbeit</Styles.SummaryLabel>
						<Styles.SummaryValue>{inProgress.length}</Styles.SummaryValue>
					</Styles.SummaryBox>

					<Styles.SummaryBox>
						<Styles.SummaryLabel>Fertig</Styles.SummaryLabel>
						<Styles.SummaryValue>{completedTasks.length}</Styles.SummaryValue>
					</Styles.SummaryBox>
				</Styles.Summary>
			</Styles.Header>

			<Styles.Tabs>
				<Styles.TabButton
					type="button"
					$active={activeTab === "all"}
					onClick={() => setActiveTab("all")}
				>
					Alle Aufträge ({jobs.length})
				</Styles.TabButton>

				<Styles.TabButton
					type="button"
					$active={activeTab === "inProgress"}
					onClick={() => setActiveTab("inProgress")}
				>
					In Arbeit ({inProgress.length})
				</Styles.TabButton>

				<Styles.TabButton
					type="button"
					$active={activeTab === "completed"}
					onClick={() => setActiveTab("completed")}
				>
					Fertig ({completedTasks.length})
				</Styles.TabButton>
			</Styles.Tabs>

			<Styles.SectionTitle>
				<GiCargoCrate />
				Auftragsliste
			</Styles.SectionTitle>

			{visibleJobs.length === 0 ? (
				<Styles.EmptyState>
					Keine Aufträge in dieser Ansicht vorhanden.
				</Styles.EmptyState>
			) : (
				<Styles.Grid>
					{visibleJobs.map(job => (
						<JobCard
							key={job.id}
							job={job}
							completed={completedIds.has(job.id)}
							onComplete={Actions.clearJobs}
						/>
					))}
				</Styles.Grid>
			)}
		</Styles.Page>
	);
};